import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  Edit as EditIcon
} from '@mui/icons-material';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const EditManufacturedProductDialog = ({ open, onClose, onUpdate, donor, vial }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    productId: '',
    quantity: 1,
    manufacturingDate: '',
    expirationDate: ''
  });

  // Fetch EPL products and initialize form when dialog opens
  useEffect(() => {
    if (open && vial) {
      fetchProducts();
      // Initialize form with vial data
      const manDate = vial.manufacturingDate ? new Date(vial.manufacturingDate).toISOString().split('T')[0] : '';
      const expDate = vial.expirationDate ? new Date(vial.expirationDate).toISOString().split('T')[0] : '';
      
      setFormData({
        productId: vial.productId || vial.productDbId || '',
        quantity: vial.quantity || 1,
        manufacturingDate: manDate,
        expirationDate: expDate
      });
    }
  }, [open, vial]);

  const fetchProducts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/products?organization=EPL&status=ACTIVE&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      const data = await response.json();
      
      // Filter for vial products (1.5ml and 3ml) - support both formats
      const vialProducts = data.data.filter(product => 
        product.volume && (
          product.volume.includes('1.5') || 
          product.volume.includes('3') ||
          product.volume.includes('1.5mL') ||
          product.volume.includes('3.0mL') ||
          product.volume.includes('3mL')
        )
      );
      
      setProducts(vialProducts);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductChange = (productId) => {
    const selectedProduct = products.find(p => p.id === productId);
    if (selectedProduct) {
      setFormData(prev => ({
        ...prev,
        productId: productId
      }));
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.productId) {
      setError('Please select a product');
      return;
    }

    if (!formData.quantity || formData.quantity <= 0) {
      setError('Vials Made must be greater than 0');
      return;
    }

    if (!formData.manufacturingDate) {
      setError('Manufacturing Date is required');
      return;
    }

    if (!formData.expirationDate) {
      setError('Expiration Date is required');
      return;
    }

    // Validate dates
    const manDate = new Date(formData.manufacturingDate);
    const expDate = new Date(formData.expirationDate);

    if (isNaN(manDate.getTime()) || isNaN(expDate.getTime())) {
      setError('Please ensure all dates are valid');
      return;
    }

    // Validate expiration date is after manufacturing date
    if (expDate <= manDate) {
      setError('Expiration date must be after manufacturing date');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const updateData = {
        productId: formData.productId,
        quantity: parseInt(formData.quantity),
        manufacturingDate: manDate.toISOString().split('T')[0], // YYYY-MM-DD format
        expirationDate: expDate.toISOString().split('T')[0] // YYYY-MM-DD format
      };

      await onUpdate(updateData);
      handleClose();
    } catch (err) {
      console.error('Error updating manufactured product:', err);
      setError(err.message || 'Failed to update manufactured product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      productId: '',
      quantity: 1,
      manufacturingDate: '',
      expirationDate: ''
    });
    setError('');
    onClose();
  };

  const selectedProduct = products.find(p => p.id === formData.productId);

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon color="primary" />
          <Typography variant="h6">Edit Manufactured Product</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Donor: {donor?.donorId} - {donor?.mareName}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Product</InputLabel>
                <Select
                  value={formData.productId}
                  label="Product"
                  onChange={(e) => handleProductChange(e.target.value)}
                >
                  {products.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.sku} - {product.name} ({product.volume || 'N/A'})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Vials Made"
                type="number"
                required
                value={formData.quantity}
                onChange={(e) => handleChange('quantity', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1 }}
                helperText="Number of vials manufactured"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Manufacturing Date"
                type="date"
                required
                value={formData.manufacturingDate}
                onChange={(e) => handleChange('manufacturingDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiration Date"
                type="date"
                required
                value={formData.expirationDate}
                onChange={(e) => handleChange('expirationDate', e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={selectedProduct ? `Product: ${selectedProduct.sku}` : ''}
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={submitting || loading}
          startIcon={submitting ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {submitting ? 'Updating...' : 'Update Product'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditManufacturedProductDialog;

